import { useState, useEffect, useRef, memo } from 'react';
import { Link } from 'react-router-dom';
import { Gauge, Info, Cog, Settings2, Car, Fuel, Zap, Snowflake } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { LiveViewers } from './LiveViewers';
import { CarfaxReport, hasCarfaxReport, isCarfaxAccidentFree, isCarfaxOneOwner } from './CarfaxReport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to normalize image URL - handles relative paths
const normalizeImageUrl = (url) => {
  if (!url) return null;
  // Convert relative paths to full URLs
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Helper to get thumbnail URL for fast grid loading
const getThumbnailUrl = (url) => {
  if (!url) return null;
  // Cloudinary auto-optimization: format, quality, width
  if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
    return url.replace('/upload/', '/upload/f_auto,q_auto,w_400/');
  }
  if (url.includes('image.autousagee.ca')) {
    return `${API}/proxy/thumbnail?url=${encodeURIComponent(url)}`;
  }
  // Handle relative paths starting with /api/uploads/
  if (url.startsWith('/api/uploads/') && !url.includes('/thumbnails/')) {
    const filename = url.split('/').pop();
    const thumbName = filename.replace(/\.(webp|jpg|jpeg|png)$/i, '_thumb.webp');
    return `${BACKEND_URL}/api/uploads/thumbnails/${thumbName}`;
  }
  // Handle full URLs with /api/uploads/
  if (url.includes('/api/uploads/') && !url.includes('/thumbnails/')) {
    const filename = url.split('/').pop();
    const thumbName = filename.replace(/\.(webp|jpg|jpeg|png)$/i, '_thumb.webp');
    return `${API}/uploads/thumbnails/${thumbName}`;
  }
  // Normalize any remaining relative paths
  return normalizeImageUrl(url);
};

// Cache overlay settings globally - fetch once, dedupe in-flight calls
let cachedOverlaySettings = null;
let overlayPromise = null;
const fetchOverlaySettings = async () => {
  if (cachedOverlaySettings !== null) return cachedOverlaySettings;
  // Dedupe concurrent calls from N cards mounting at once — all await the same promise.
  if (overlayPromise) return overlayPromise;
  overlayPromise = (async () => {
    try {
      const res = await axios.get(`${API}/marketing/overlay`);
      cachedOverlaySettings = res.data;
    } catch {
      cachedOverlaySettings = { enabled: false, image_url: null };
    }
    return cachedOverlaySettings;
  })();
  return overlayPromise;
};

// Memoized VehicleCard for better performance
export const VehicleCard = memo(({ vehicle, priority = false }) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [imageHovered, setImageHovered] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [overlaySettings, setOverlaySettings] = useState({ enabled: false, image_url: null });
  const cardRef = useRef(null);
  
  // 3:2 ratio placeholder - fits perfectly
  const defaultImage = 'https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460688/entrepot/placeholders/new_arrival_placeholder.webp';
  const photoCount = vehicle.images?.length || 0;

  // Lazy load with IntersectionObserver
  useEffect(() => {
    if (priority || isInView) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px', threshold: 0.01 }
    );
    
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [priority, isInView]);

  // Fetch overlay settings once
  useEffect(() => {
    fetchOverlaySettings().then(setOverlaySettings);
  }, []);

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === 0 || price === 0.0 || !price) {
      return t('vehicle.callDealer');
    }
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency', currency: 'CAD', minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => new Intl.NumberFormat('fr-CA').format(mileage);

  const getTransmissionLabel = (trans) => {
    if (!trans) return t('vehicle.automatic');
    const val = trans.toLowerCase();
    if (val === 'automatic' || val === 'auto' || val === 'automatique') return t('vehicle.automatic');
    if (val === 'manual' || val === 'manuelle' || val === 'manuel') return t('vehicle.manual');
    if (val === 'cvt') return t('vehicle.cvt');
    return t('vehicle.automatic');
  };

  const getFuelLabel = (f) => {
    if (!f) return '-';
    const val = f.toLowerCase();
    const fuelMap = { 
      gasoline: t('vehicle.gasoline'), essence: t('vehicle.gasoline'),
      diesel: t('vehicle.diesel'), 
      electric: t('vehicle.electric'), électrique: t('vehicle.electric'), electrique: t('vehicle.electric'),
      hybrid: t('vehicle.hybrid'), hybride: t('vehicle.hybrid')
    };
    return fuelMap[val] || f;
  };
  
  const getDrivetrainLabel = (d) => {
    if (!d) return '-';
    const val = d.toLowerCase();
    if (val === 'awd' || val === '4wd') return t('vehicle.awd');
    if (val === 'fwd') return t('vehicle.fwd');
    if (val === 'rwd') return t('vehicle.rwd');
    return d;
  };
  
  const getBodyTypeLabel = (b) => {
    if (!b) return '-';
    const val = b.toLowerCase();
    const bodyMap = { 
      sedan: t('vehicle.sedan'), berline: t('vehicle.sedan'),
      suv: t('vehicle.suv'), vus: t('vehicle.suv'),
      truck: t('vehicle.truck'), camion: t('vehicle.truck'),
      coupe: t('vehicle.coupe'), coupé: t('vehicle.coupe'),
      hatchback: t('vehicle.hatchback'), hayon: t('vehicle.hatchback'),
      van: t('vehicle.van'), fourgonnette: t('vehicle.van'),
      convertible: t('vehicle.convertible')
    };
    return bodyMap[val] || b;
  };

  return (
    <div ref={cardRef} className="relative group bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm transition-[box-shadow,border-color,transform] duration-300 hover:shadow-xl hover:shadow-blue-500/10 hover:border-[#3b82f6]/50 hover:-translate-y-1">
      <Link 
        to={`/vehicle/${vehicle.id}`}
        className="block"
        data-testid={`vehicle-card-${vehicle.id}`}
      >
        {/* Image Container with fixed aspect ratio to prevent CLS */}
        <div 
          className="relative overflow-hidden bg-slate-100"
          style={{ aspectRatio: '16/11' }}
          onMouseEnter={() => setImageHovered(true)}
          onMouseLeave={() => setImageHovered(false)}
        >
          {/* Live Viewers Badge */}
          <LiveViewers vehicleId={vehicle.id} variant="card" />
          
          {/* Skeleton placeholder - always present until loaded */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gradient-to-br from-slate-200 to-slate-100 animate-pulse" />
          )}
          
          {/* Only render image when in viewport */}
          {isInView && !vehicle.video_url && (
            <img
              src={getThumbnailUrl(vehicle.images?.[0]) || defaultImage}
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
              width={400}
              height={275}
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              fetchpriority={priority ? 'high' : 'auto'}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => { e.target.src = defaultImage; setImageLoaded(true); }}
              className={`w-full h-full object-cover transition-transform duration-300 ${
                isInView && imageLoaded ? 'opacity-100' : 'opacity-0'
              } ${imageHovered ? 'scale-105' : ''}`}
              style={{ position: 'absolute', inset: 0 }}
            />
          )}
          
          {/* Video autoplay when visible */}
          {vehicle.video_url && (
            <video
              ref={(el) => {
                if (!el) return;
                const observer = new IntersectionObserver(
                  ([entry]) => {
                    if (entry.isIntersecting) {
                      el.play().catch(() => {});
                    } else {
                      el.pause();
                    }
                  },
                  { threshold: 0.3 }
                );
                observer.observe(el);
              }}
              src={vehicle.video_url.includes('cloudinary.com') 
                ? vehicle.video_url.replace('/upload/', '/upload/w_360,q_auto:eco,br_500k,f_mp4/') 
                : vehicle.video_url}
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          
          {/* Overlay/Watermark */}
          {overlaySettings.enabled && overlaySettings.image_url && (
            <img 
              src={overlaySettings.image_url} 
              alt="" 
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              loading="lazy"
            />
          )}
          
          {/* Photo count on hover - minimal DOM */}
          {imageHovered && photoCount > 0 && (
            <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded-lg flex items-center gap-1.5 text-xs font-medium">
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>{photoCount}</span>
            </div>
          )}
          
          {/* Video play icon */}
          {vehicle.video_url && (
            <div className="absolute bottom-2 right-2 bg-black/70 text-white p-1.5 rounded-full" data-testid={`video-badge-${vehicle.id}`}>
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          )}
          
          {/* Status badges */}
          {vehicle.status === 'sold' && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="font-bold text-2xl text-white">{t('vehicle.sold')}</span>
            </div>
          )}
          {vehicle.status === 'reserved' && (
            <span className="absolute top-2 left-2 bg-purple-600 text-white text-xs px-3 py-1 rounded-full font-bold">
              {t('vehicle.reserved')}
            </span>
          )}
        </div>

        {/* Content - minimal re-renders */}
        <div className="p-3 bg-white">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0">
              <h3 className="font-bold text-slate-900 text-xs sm:text-sm leading-tight group-hover:text-[#3b82f6] transition-colors">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h3>
              <p className="text-slate-500 text-[10px] sm:text-xs uppercase mt-0.5 line-clamp-1">{vehicle.trim}{vehicle.title ? ` | ${vehicle.title}` : ''}</p>
            </div>
            {vehicle.regular_price && vehicle.regular_price > vehicle.price ? (
              <div className="bg-[#0c2340]/5 border border-[#0c2340]/20 rounded-lg px-2 py-1.5 text-center flex-shrink-0" data-testid={`vehicle-promo-badge-${vehicle.id}`}>
                <span className="inline-block bg-[#0c2340] text-white text-[7px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider mb-0.5">Promo</span>
                <p className="text-[8px] text-slate-400 line-through leading-none">{formatPrice(vehicle.regular_price)}</p>
                <p className="font-black text-sm text-[#0c2340] leading-tight" data-testid={`vehicle-price-${vehicle.id}`}>{formatPrice(vehicle.price)}</p>
                <p className="text-[7px] text-emerald-600 font-semibold leading-none">Économisez {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(vehicle.regular_price - vehicle.price)}</p>
              </div>
            ) : (
              <div className="text-right">
                <span className="font-bold text-[#3b82f6] text-mobile-card-title sm:text-sm whitespace-nowrap block" data-testid={`vehicle-price-${vehicle.id}`}>
                  {formatPrice(vehicle.price)}
                </span>
              </div>
            )}
          </div>

          {/* Winter tires / SAAQ badges */}
          {(vehicle.winter_tires_included || vehicle.saaq_registration_handled) && (
            <div className="flex flex-wrap gap-1 mb-2">
              {vehicle.winter_tires_included && (
                <span className="inline-flex items-center gap-1 bg-sky-50 text-sky-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-sky-200">
                  <Snowflake className="h-2.5 w-2.5" />
                  {t('vehicle.winterTiresBadge', 'Pneus hiver inclus')}
                </span>
              )}
              {vehicle.saaq_registration_handled && (
                <span className="inline-flex items-center bg-emerald-50 text-emerald-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-emerald-200">
                  {t('vehicle.saaqBadge', 'SAAQ inclus')}
                </span>
              )}
            </div>
          )}

          <div className="flex items-center justify-between text-slate-500 text-mobile-xs sm:text-xs">
            <div className="flex items-center gap-1 bg-slate-100 group-hover:bg-[#3b82f6]/20 px-2 py-1 rounded transition-colors">
              <Gauge className="h-3 w-3 text-slate-400 group-hover:text-[#3b82f6] transition-colors" />
              <span>{formatMileage(vehicle.mileage)} km</span>
            </div>
            {/* Info Icon - on the right side */}
            <div 
              className="relative flex items-center"
              onMouseEnter={() => setShowInfo(true)}
              onMouseLeave={() => setShowInfo(false)}
            >
              <span 
                className="text-slate-400 hover:text-[#3b82f6] cursor-pointer transition-colors"
                onClick={(e) => e.preventDefault()}
                aria-label="Plus d'informations"
              >
                <Info className="h-4 w-4" />
              </span>

              {/* Info Tooltip - centered */}
              {showInfo && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 bg-white rounded-xl p-4 shadow-2xl z-50 text-sm border border-slate-200">
                  <h4 className="font-bold text-slate-900 mb-3 text-sm">{t('vehicle.vehicleInfo')}</h4>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="flex items-center gap-1.5 text-slate-500 mb-0.5">
                        <Gauge className="h-3.5 w-3.5" />
                        <span>{t('vehicle.mileage')}</span>
                      </div>
                      <p className="font-semibold text-slate-900">{formatMileage(vehicle.mileage)} {t('vehicle.km')}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-slate-500 mb-0.5">
                        <Cog className="h-3.5 w-3.5" />
                        <span>{t('vehicle.engine')}</span>
                      </div>
                      <p className="font-semibold text-slate-900">{vehicle.engine || '-'}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-slate-500 mb-0.5">
                        <Settings2 className="h-3.5 w-3.5" />
                        <span>{t('vehicle.transmission')}</span>
                      </div>
                      <p className="font-semibold text-slate-900">{getTransmissionLabel(vehicle.transmission)}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-slate-500 mb-0.5">
                        <Car className="h-3.5 w-3.5" />
                        <span>{t('vehicle.category')}</span>
                      </div>
                      <p className="font-semibold text-slate-900">{getBodyTypeLabel(vehicle.body_type)}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-slate-500 mb-0.5">
                        <Fuel className="h-3.5 w-3.5" />
                        <span>{t('vehicle.fuel')}</span>
                      </div>
                      <p className="font-semibold text-slate-900">{getFuelLabel(vehicle.fuel_type)}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-slate-500 mb-0.5">
                        <Zap className="h-3.5 w-3.5" />
                        <span>{t('vehicle.drivetrain')}</span>
                      </div>
                      <p className="font-semibold text-slate-900">{getDrivetrainLabel(vehicle.drivetrain)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>
      {(hasCarfaxReport(vehicle) || isCarfaxOneOwner(vehicle) || isCarfaxAccidentFree(vehicle)) && (
        <div className="px-3 py-2.5 border-t border-slate-100 bg-white" data-testid={`vehicle-card-carfax-${vehicle.id}`}>
          <CarfaxReport vehicle={vehicle} height={30} />
        </div>
      )}
    </div>
  );
});

VehicleCard.displayName = 'VehicleCard';
